<!--

Items documentation:

Example of buying 2 products:

items = [{count: 2, product_id: 17, price: 42}]

For one resource booking:

    count: 1
    extra_person (optional)
    optional_resource (optional)
    rbservice_id
    service (object)
    reservation (id of reservation)
    slots: [
        start
        end
        rbservice_id
        staff_id
    ],
    context (optional context),
    extended_info (answer to questions)
    tmp_id (in order to link additional products)

Events:
    almostdone: The purchase is complete. The "Done"-page is shown
    loading: Checkout is in progress (true / false)
    cost: The total cost as returned by the server
    readyToPay: If checkout button can be clicked (true / false)
    readyToPayExceptPaymentMethod: If everything except the payment method selection is done (true / false)
    changeNumberOfItems: Number of items in basket changed. Parameters: item, count
    changeItemUsers: (item, users)
    mustSelectUsers: If users must be selected for some item (true / false)
    receipt: (bundle_id) - Emitted when purchase is done and there is a receipt available. Parameter is bundle ID

Templates:
    done: Replaces the entire "Done"-page. Parameters: context: the context of the resource booking (this template is only used for resource bookings)
    donetext: Replaces a placeholder which otherwise says "Tack för ditt köp!"
    donesummarytitle: Replaces a placeholder which otherwise says "Du köpte"

-->


<template>

    <v-container fluid class="py-0 fill-height" :class="{'primary': done, 'z-no-x-scroll': dialog}">
        <v-row v-if="!done && showBackLink">
            <v-col>
                <span @click="$emit('done')" class="z-clickable primary--text text-decoration-underline ml-4">{{$translate('Back')}}</span>
            </v-col>
        </v-row>

        <v-row v-if="!done" class="fill-height">
            <v-col :md="showSummary && showPaymentPart ? 6 : 12" cols=12 order="last" v-if="showPaymentPart" :class="{'overflow-y-auto' : dialog, 'z-max-height-70vh-desktop': dialog}">

                <slot name="content" v-if="$slots.content" />

                <portal :to="contentTarget" :disabled="!contentTarget">
                    <zoezi-identification v-if="!$store.state.user && !$store.state.isCheckingIfLoggedIn" :title="identificationTitle" :sub-title="identificationSubTitle" />

                    <v-expansion-panels v-if="$store.state.user" class="mb-4" style="box-shadow: none" :value="0">
                        <v-expansion-panel elevation="0">
                            <v-expansion-panel-header class="pa-2" color="white">
                                <div class="pa-2 d-flex">
                                    <div class="d-flex align-center">
                                        <v-icon color="primary">mdi-account-star-outline</v-icon>
                                    </div>
                                    <div class="ml-2 flex-grow-1">
                                        <span class="font-weight-bold">{{$store.state.user.name}}</span><span @click.stop="logout" class="z-clickable primary--text text-decoration-underline ml-4">{{$translate('Not you?')}}</span>
                                    </div>
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content class="z-dense">
                                <v-row no-gutters>
                                    <v-col v-text="$store.state.user.mail" />
                                </v-row>
                                <v-row no-gutters>
                                    <v-col cols=6 v-text="$store.state.user.address" />
                                    <v-col cols=6 v-text="$store.state.user.phone" />
                                </v-row>
                                <v-row no-gutters>
                                    <v-col v-text="($store.state.user.zipCode||'') + ' ' + ($store.state.user.city||'')"/>
                                </v-row>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <div class="elevation-2 pt-4 px-2" v-if="$store.state.user && cancelRules">
                        <div class="d-flex z-clickable" @click="showCancelRules = true">
                            <v-icon color="error">mdi-sticker-alert-outline</v-icon>
                            <div class="ml-2 error--text flex-grow-1">{{$translate('Cancellation policy')}}</div>
                            <v-icon>mdi-chevron-right</v-icon>
                        </div>

                        <v-checkbox v-model="cancelRulesAccepted" color="primary" :label="$translate('Accept cancellation policy')" />

                        <cancel-rules-dialog v-model="showCancelRules" :cancel-rules="cancelRules" show-accept-button v-if="showCancelRules" @accepted="cancelRulesAccepted = true"/>
                    </div>

                    <div class="zoezi-checkout-payment elevation-2" v-if="$store.state.user">
                        <div class="d-flex pt-4 pl-4" v-if="totalFromServer > 0 || cartHasContinuousPayment">
                            <div class="d-flex align-center">
                                <v-icon color="primary">mdi-credit-card-check-outline</v-icon>
                            </div>
                            <div class="ml-2 flex-grow-1 black--text" style="font-size: 0.9375rem">
                                <span class="font-weight-bold">{{$translate('How would you like to pay?')}}</span>
                            </div>
                        </div>

                        <v-expand-transition>
                            <div class="zoezi-checkout-payment-method-selection mx-2" v-if="hasWebShop && (totalFromServer > 0 || cartHasContinuousPayment) && $store.state.settings">
                                <v-form v-model="formValid" @submit.prevent="" :disabled="loading && !adyenProcess">
                                    <v-radio-group v-model="selectedMethodId" class="mt-0">
                                        <v-alert v-if="methods && methods.length > 0 && sortedMethods.length === 0" type="info" text dense class="my-2">
                                            {{$translate('No payment methods available for this payment')}}
                                        </v-alert>
                                        <div v-for="method in sortedMethods" :key="method.id" class="zoezi-checkout-payment-method z-rounded-border my-1">
                                            <div class="d-flex ml-2">
                                                <v-radio :label="getMethodName(method)" color="primary" :value="method.id" class="flex-grow-1 mb-0" />
                                                <img :src="method.imagekey ? $api.getImageUrl(method.imagekey, 100) : $api.getAssetUrl(method.imageSrc)" class="zoezi-checkout-payment-method-image ma-1" style="height: 32px; width: 32px" />
                                            </div>

                                            <div v-if="method.subtitle" class="ml-10 text--secondary z-small-font">{{method.subtitle}}</div>

                                            <div v-if="method.id == selectedMethodId" class="mx-2 ml-10">
                                                <div v-if="!$store.state.user.mail && selectedMethodId != 'invoice'">
                                                    <form-field :field="mailField" star v-model="mailField.value" />
                                                </div>
                                                <div v-if="requirePhoneField && !$store.state.user.phone && selectedMethodId != 'invoice'">
                                                    <form-field :field="phoneField" star v-model="phoneField.value" />
                                                </div>

                                                <template v-if="method.id == 'stripe'">
                                                    <div>
                                                        <stripe-elements ref="stripe" label="Kortnummer" @change="cardChanged" autofocus />
                                                        <v-checkbox :label="$translate('Save card information for future purchases')" v-model="saveCard" />
                                                        <v-alert type="info">
                                                            {{$translate('SAVE_CARD_INFO')}}
                                                        </v-alert>
                                                    </div>
                                                </template>
                                                <template v-else-if="method.id == 'stripe_saved'">
                                                    <stripe-elements ref="stripe" label="Kortnummer" v-show="false" />
                                                </template>
                                                <template v-else-if="method.id == 'directdebit'">
                                                    <sign-direct-debit-consent :legal-entity-id="legalEntityId" />
                                                </template>
                                                <template v-if="method.id.startsWith('adyen-') && !method.id.startsWith('adyen-card:saved')">
                                                    <AdyenPayment ref="adyen" :method="method.id" :available-methods="methods" @change="cardChanged" />
                                                </template>
                                                <template v-else-if="method.id == 'invoice'">
                                                    <v-alert v-if="($store.state.user.personalCodeNumber || (invoice.personalCodeNumber.value && isValidPersonalCode(invoice.personalCodeNumber.value))) && !ageOkForInvoice" color="error" class="white--text">
                                                        <v-icon color="white">mdi-information-outline</v-icon>
                                                        {{$translate('You must be over 18 years old to shop with an invoice.')}}
                                                    </v-alert>
                                                    <div v-if="$store.state.settings && $store.state.settings.invoiceFee > 0" class="text--secondary z-small-font">
                                                        {{$translate('A billing fee of {0} will be added').format(formatPrice($store.state.settings.invoiceFee))}}
                                                    </div>
                                                    <div v-if="invoiceChangeAddress" class="mt-2">
                                                        <form-field :field="invoice.personalCodeNumber" star  v-if="!$store.state.user.personalCodeNumber" v-model="invoice.personalCodeNumber.value" />
                                                        <form-field :field="invoice.name" star v-model="invoice.name.value" />
                                                        <form-field :field="invoice.mail" star v-model="invoice.mail.value" />
                                                        <form-field :field="invoice.address" star v-model="invoice.address.value" />
                                                        <v-row no-gutters>
                                                            <v-col cols=6>
                                                                <form-field :field="invoice.zipCode" star v-model="invoice.zipCode.value"  class="mr-2" />
                                                            </v-col>
                                                            <v-col cols=6>
                                                                <form-field :field="invoice.city" star v-model="invoice.city.value" class="ml-2" />
                                                            </v-col>
                                                        </v-row>
                                                    </div>
                                                    <div v-else>
                                                        <v-row no-gutters>
                                                            <v-col cols=6>
                                                                {{$store.state.user.firstname}} {{$store.state.user.lastname}}
                                                            </v-col>
                                                            <v-col cols=6>
                                                                {{$store.state.user.personalCodeNumber}}
                                                            </v-col>
                                                        </v-row>
                                                        <v-row no-gutters>
                                                            <v-col cols=6>
                                                                {{$store.state.user.address}}
                                                            </v-col>
                                                            <v-col cols=6>
                                                                <div v-if="$store.state.user.mail">{{$store.state.user.mail}}</div>
                                                                <div v-else class="text--secondary">{{$translate('Email is missing')}}</div>
                                                            </v-col>
                                                        </v-row>
                                                        <v-row no-gutters>
                                                            <v-col cols=6>
                                                                {{$store.state.user.zipCode}} {{$store.state.user.city}}
                                                            </v-col>
                                                            <v-col cols=6>
                                                                <div v-if="$store.state.user.phone">{{$store.state.user.phone}}</div>
                                                                <div v-else class="text--secondary">{{$translate('Phone is missing')}}</div>
                                                            </v-col>
                                                        </v-row>
                                                        <span class="z-clickable primary--text text-decoration-none" @click="invoiceChangeAddress = true">{{$translate('Change')}}</span>
                                                    </div>
                                                    <form-field v-if="selectedMethod.company_invoice" :field="companyInvoice.enable" v-model="companyInvoice.enable.value" />
                                                    <div v-if="companyInvoice.enable.value && selectedMethod.company_invoice">
                                                        <form-field :field="companyInvoice.name" star v-model="companyInvoice.name.value" />
                                                        <form-field :field="companyInvoice.mail" star v-model="companyInvoice.mail.value" />
                                                        <form-field :field="companyInvoice.address" star v-model="companyInvoice.address.value" />
                                                        <v-row no-gutters>
                                                            <v-col cols=6>
                                                                <form-field :field="companyInvoice.zipCode" star v-model="companyInvoice.zipCode.value"  class="mr-2" />
                                                            </v-col>
                                                            <v-col cols=6>
                                                                <form-field :field="companyInvoice.city" star v-model="companyInvoice.city.value" class="ml-2" />
                                                            </v-col>
                                                        </v-row>
                                                    </div>
                                                </template>
                                                <template v-else-if="method.id == 'epassi'">
                                                    <Epassi :transaction_id="epassiTransactionId" :cartName="cartName" @failed="() => loading = false"/>
                                                </template>
                                            </div>
                                        </div>
                                    </v-radio-group>
                                </v-form>
                            </div>
                        </v-expand-transition>

                        <div v-if="hasWebShop && isNaN(totalFromServer) && !gettingTotalsFromServer && itemsForTotal.length > 0" class="pa-2">
                            <v-icon color="error">mdi-alert</v-icon>{{$translate('The shopping cart seems to be broken. It is not possible to complete the purchase.')}}
                        </div>
                    </div>

                    <terms-notice v-if="$store.state.user" action-text="When you complete an order, you agree to" />

                    <v-alert v-if="invalidPurchaseAgeItems.length" dense text type="error" border="left" icon="mdi-alert-circle" class="mx-4">
                        {{ $translate('Invalid age for purchase') }}
                    </v-alert>

                    <v-btn v-if="$store.state.user && showPaymentPart && showPaymentButton" :disabled="!readyToPay" block color="#7bbd1b" class="rounded-0 zoezi-button-thin" :dark="readyToPay" x-large :loading="loading" @click="completePurchase">
                        <template v-if="isNaN(totalFromServer)">
                        </template>
                        <template v-else-if="totalFromServer > 0">
                            {{$translate('Pay')}} {{totalFromServer | price}}
                        </template>
                        <template v-else>
                            {{$translate('Confirm Purchase')}}
                        </template>
                    </v-btn>
                </portal>
            </v-col>
            <v-col :md="showSummary && showPaymentPart ? 6 : 12" cols=12 v-if="showSummary">
                <div class="d-flex justify-end mb-1 mr-2">
                    <span @click="clearCartAndClose" class="z-clickable text-decoration-underline text--secondary">
                        {{$translate('Clear cart')}}
                    </span>
                </div>
                <div class="zoezi-checkout-summary" ref="summary">
                    <checkout-reservation-time
                        :items="computedItems"
                        :disabled="loading"
                        @timeout="$emit('reservation-timeout')"
                        @isExpired="hasExpiredItems = $event" />

                    <div v-if="isLoadingProducts" class="d-flex justify-center fill-width">
                        <v-progress-circular indeterminate color="primary" size="64" width="2"  />
                    </div>

                    <v-expansion-panels accordion v-model="expandedItems" multiple v-if="visibleItems.length">
                        <transition-group
                            class="z-contents"
                            enter-active-class="animate__animated animate__bounceInLeft"
                            leave-active-class="animate__animated animate__bounceOutRight"
                        >
                            <checkout-item-row
                                v-for="item in visibleItemsTop" :key="getItemKey(item, 'top')"
                                :item="item"
                                :total-from-server-object="totalFromServerObject"
                                :user-read-only="userReadOnly"
                                :value-card-cannot-be-used="valueCardCannotBeUsed"
                                :items-contain-periodic-cards="itemsContainPeriodicCards"
                                :count-read-only="countReadOnly"
                                :summary-width="summaryWidth"
                                @attemptChangeNumberOfItems="attemptChangeNumberOfItems"
                                @changeItemUsers="(item, users) => $emit('changeItemUsers', item, users)"
                                @changeIncludedProductVariant="(item, index, productId, value) => $emit('changeIncludedProductVariant', item, index, productId, value)"
                                @removeItem="removeItem"
                            />

                            <div key="divider" v-if="visibleItemsTop.length > 0 && visibleItemsFromCart.length > 0" class="mt-2">
                                <div class="text-center">{{$translate('Items in the shopping cart')}}</div>
                            </div>

                            <checkout-item-row
                                v-for="item in visibleItemsFromCart" :key="getItemKey(item, 'cart')"
                                :item="item"
                                :total-from-server-object="totalFromServerObject"
                                :user-read-only="userReadOnly"
                                :value-card-cannot-be-used="valueCardCannotBeUsed"
                                :items-contain-periodic-cards="itemsContainPeriodicCards"
                                :count-read-only="countReadOnly"
                                :summary-width="summaryWidth"
                                @attemptChangeNumberOfItems="attemptChangeNumberOfItems"
                                @changeItemUsers="(item, users) => $emit('changeItemUsers', item, users)"
                                @changeIncludedProductVariant="(item, index, productId, value) => $emit('changeIncludedProductVariant', item, index, productId, value)"
                                @removeItem="removeItem"
                            />

                            <v-expansion-panel key="sum" v-if="showSum">
                                <v-expansion-panel-header class="pa-2">
                                    <div class="d-flex flex-column">
                                        <div class="d-flex">
                                            <div class="flex-grow-1"><span class="font-weight-bold">{{$translate('Sum')}}&nbsp;</span><span class="text--secondary" style="font-size: smaller">{{$translate('incl. VAT')}}</span></div>
                                            <div class="font-weight-bold" v-if="!isNaN(totalFromServer)">{{totalFromServer | price}}</div>
                                        </div>
                                        <div class="d-flex" v-if="cartHasContinuousPayment">
                                            <v-spacer/>
                                            <div>{{$translate('Thereafter')}} <span v-for="(sub,index) in totalFromServerObject.subscriptions" :key="index">{{sub.price | price}} per {{$translate(sub.price_type).toLowerCase()}}<span v-if="index != totalFromServerObject.subscriptions.length-1">, </span></span></div>
                                        </div>
                                    </div>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content class="pa-2">
                                    <div class="ml-10" v-if="totalFromServerObject && totalFromServerObject.vats">
                                        <div class="d-flex flex-column">
                                            <div v-for="vat in Object.keys(totalFromServerObject.vats).filter(vat => totalFromServerObject.vats[vat]).sort((a,b) => a - b)" :key="vat" class="d-flex flex-row">
                                                <div class="flex-grow-1">{{$translate('VAT')}} {{vat}}%</div><div>{{totalFromServerObject.vats[vat] | price}}</div>
                                            </div>
                                            <div class="d-flex flex-row">
                                                <div class="flex-grow-1">{{$translate('Total VAT')}}</div><div>{{Object.values(totalFromServerObject.vats).reduce((a,b) => a + b, 0) | price}}</div>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </transition-group>
                    </v-expansion-panels>
                    <v-alert v-if="totalFromServerObject && totalFromServerObject.warnings && totalFromServerObject.warnings.length" type="info" text dense class="mt-2 mx-2">
                        {{ $translate('Discount calculation may be affected. Regular prices are shown.') }}
                    </v-alert>
                    <div v-if="canEnterDiscountOrGiftcard">
                        <v-form @submit.prevent="addCode" :disabled="loading">
                            <v-text-field :label="$translate('Enter discount code or gift card')" v-model.trim="code" :loading="loadingCode" :disabled="loadingCode" :error-messages="discountErrorText">
                                <template #append-outer>
                                    <zoezi-button  v-if="code" @click="addCode" :text="$translate('Use code')" class="mt-n1" thin color="white" text-color="black" :loading="loadingCode" />
                                </template>
                            </v-text-field>
                        </v-form>
                    </div>
                    <div v-if="hasMultipleLegalEntities && cartLegalEntityName" class="text--secondary mt-1" style="font-size: 0.75rem;">
                        {{$translate('Bought from')}}: {{cartLegalEntityName}}
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- Done -->
        <template v-else>
            <template v-if="computedItems.find(x => x.type == 'resourcebooking')">
                <slot name="done" v-bind:context="computedItems.find(x => x.type == 'resourcebooking').context" />
            </template>
            <v-sheet v-else color="primary" ref="done">
                <div class="mb-4 d-flex justify-center white--text">
                    <v-icon x-large class="white--text">mdi-checkbox-marked-circle-outline</v-icon><span class="text-h4 ml-4">{{$translate('Done!')}}</span>
                </div>

                <div class="d-flex">
                    <v-avatar size="60" class="mx-2">
                        <v-auto-img :src="$root.siteconfig.appbar.props.logo" />
                    </v-avatar>
                    <v-card class="ma-2">
                        <v-card-text>
                            <slot name="donetext" v-if="$slots.donetext" />
                            <div v-else><span class="font-weight-bold">{{$translate('Thank you for your purchase!')}}</span></div>
                        </v-card-text>
                    </v-card>
                </div>

                <div class="d-flex">
                    <v-card class="ma-2" min-width="80%">
                        <v-card-text>
                            <slot name="donesummarytitle" v-if="$slots.donesummarytitle" />
                            <div v-else class="d-flex font-weight-bold">
                                <div class="flex-grow-1">{{$translate('You bought')}}</div>
                            </div>
                            <div v-for="(crow,index) in orderconfirmation" :key="index">
                                {{crow.title}}
                            </div>

                            <template v-if="totalFromServer > 0">
                                <v-divider />
                                <div class="d-flex">
                                    <div class="flex-grow-1">{{$translate('Sum')}}</div>
                                    <div>{{totalFromServer | price}}</div>
                                </div>
                            </template>

                            <div v-if="paidByMethod == 'invoice'">
                                <v-alert type="info">
                                    {{$translate('The invoice is sent to the provided email address.')}}
                                    <br/>
                                    {{$translate('You can also retrieve it later under "My Pages">"Payments".')}}
                                </v-alert>
                            </div>
                            <div v-else-if="paidByMethod == 'directdebit'">
                                <v-alert type="info">
                                    {{$translate('You can view the direct debit invoice under "My pages">"Payments" afterwards.')}}
                                </v-alert>
                            </div>
                            <div v-else>
                                <v-btn v-if="orderconfirmation.length && orderconfirmation[0].bundle_id" @click="getReceipt" color="primary" class="zoezi-button-thin" block>
                                    <v-icon left>mdi-file-document-outline</v-icon>
                                    {{$translate('Get receipt')}}
                                </v-btn>
                            </div>

                        </v-card-text>
                    </v-card>
                    <v-avatar size=64 color="white" class="elevation-2">
                        <v-icon x-large color="primary">mdi-cart-outline</v-icon>
                    </v-avatar>
                </div>
            </v-sheet>
        </template>

        <v-dialog max-width="400" v-model="showSwishDialog" persistent v-if="swishToken">
            <v-card>
                <v-card-title class="headline align-start flex-column pa-2 primary primarytext--text">
                    <div class="font-weight-bold fill-width">
                        {{$translate('Pay with Swish')}}
                        <v-btn icon @click="cancelSwish" class="float-right"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                    </div>
                </v-card-title>

                <v-card-text class="d-flex flex-column align-center">
                    <img :src="swishQR" width="300" height="300" />

                    {{$translate('Open the Swish app and scan the QR code')}}<span v-if="isMobileWithSwish"> {{$translate('or press the button to open Swish.')}}</span>
                </v-card-text>

                <v-card-actions v-if="isMobileWithSwish">
                    <v-btn @click="openSwish" color="primary" block class="zoezi-button-thin">{{$translate('Open Swish')}}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import StripeElements from '../components/StripeElements.vue'
import { mapGetters } from 'vuex';
import AdyenPayment from '../components/AdyenPayment.vue'
import FormField from '../components/FormField.vue'
import ZoeziIdentification from '../views/Identification.vue'
import SignDirectDebitConsent from '../views/SignDirectDebitConsent.vue'
import CancelRulesDialog from '../views/CancelRulesDialog.vue'
import {decodeJson2, isValidPersonalCode, getAgeFromPersonalCodeNumber, downloadFile, isMobile, isExpoApp, getCartItems, getName, formatPrice} from '../util.js'
import {isObject} from 'lodash';
import CheckoutItemRow from '../components/CheckoutItemRow.vue'
import CheckoutReservationTime from '../components/CheckoutReservationTime.vue'
import * as Sentry from '@sentry/browser'
import Epassi from '../components/Epassi.vue'
import TermsNotice from '../components/TermsNotice.vue'
import paymentMethodFiltering from '../mixins/paymentMethodFiltering.js'

export default {
    name: 'zoezi-checkout',

    mixins: [paymentMethodFiltering],

    zoezi: {
        title: 'Utcheckning',
        icon: 'mdi-cash-register',
        internal: true
    },

    props: {
        items: {
            title: 'Varor',
            type: Array
        },
        useCart: {
            title: 'Använd varukorg',
            type: Boolean,
            default: false
        },
        cartName: {
            title: 'Varukorgsnamn',
            type: String,
            default: ''
        },
        expandFirstItem: {
            title: 'Expandera första varan',
            type: Boolean,
            default: false
        },
        showBackLink: {
            title: 'Visa tillbakalänk',
            type: Boolean,
            default: false
        },
        showSummary: {
            title: 'Visa varukorgen',
            type: Boolean,
            default: true
        },
        showPaymentPart: {
            title: 'Visa betaldelen',
            type: Boolean,
            default: true
        },
        showPaymentButton: {
            title: 'Visa betalknappen',
            type: Boolean,
            default: true
        },
        contentTarget: {
            title: 'Visa betaldelen på annat ställe med <portal>',
            type: String
        },
        countReadOnly: {
            title: 'Tillåt inte att ändra antal',
            type: Boolean,
            default: false
        },
        userReadOnly: {
            title: 'Tillåt inte att ändra personer',
            type: Boolean,
            default: false
        },
        cancelRules: {
            title: 'Avbokningsvillkor',
            type: Object
            /*
                Example:
                {
                    canCancel: true,
                    lastCancelTime: DateTime
                    rules: [
                        {time: {value: 1, timeunit: 'week', relative: 'beforestart'}, cost_percent: 50, cost_fixed: 0 },
                        {time: {value: 2, timeunit: 'week', relative: 'beforestart'}, cost_fixed: 100 }
                    ]
                }
            */
        },
        showSum: {
            title: 'Visa summa-rad',
            type: Boolean,
            default: true
        },
        forceDone: { // Forces checkout component to be in done/undone state
            type: Boolean,
            default: false
        },
        siteId: {
            title: 'Anläggning',
            type: Number,
            default: 1
        },
        identificationTitle: {
            title: 'Titel för identifikation',
            type: String
        },
        identificationSubTitle: {
            title: 'Undertitel för identifikation',
            type: String
        },
        newStyleDoneDialog: {
            type: Boolean,
            default: false
        },
        dialog: {
            title: 'Kommer visas i en dialogruta',
            type: Boolean,
            default: false
        },
        forceLoading: {
            type: Boolean,
            default: false
        },
        legalEntityId: {
            type: Number,
            default: null
        }
    },

    components: {
        StripeElements,
        FormField,
        ZoeziIdentification,
        SignDirectDebitConsent,
        CancelRulesDialog,
        AdyenPayment,
        CheckoutItemRow,
        CheckoutReservationTime,
        Epassi,
        TermsNotice
    },

    data: () => ({
        animated: false,
        done: false,
        orderconfirmation: null,

        expandedItems: [],

        methods: [],
        selectedMethodId: null,
        paidByMethod: null,
        formValid: false,
        cardValid: false,
        loading: false,
        loadingCode: false,
        adyenProcess: false,
        code: '',
        codeError: false,
        discountError: false,
        discountErrorCode: '',
        multiDiscountError: false,
        wrongLegalEntityError: false,
        saveCard: false,
        savedCard: {},
        addedGiftCards: [],
        addedDiscounts: [],
        valueCardRemoved: false,
        valueCardCannotBeUsed: false,

        totalFromServer: NaN,
        totalFromServerObject: {},
        gettingTotalsFromServer: 0,
        lastTotalsValueUsed: null,

        mailField: {type: 'email', title: 'E-mail', required: true, readonly: false, hint: 'The receipt is sent to the specified e-mail address', value: ''},
        phoneField: {type: 'phone', title: 'Phone', required: true, readonly: false, value: ''},

        invoiceChangeAddress: false,

        invoice: {
            personalCodeNumber: {type: 'personalCodeNumber', title: 'Personal code number', required: true, readonly: false, value: ''},
            name: {type: 'text', title: 'First & last name', required: true, autofocus: true, readonly: false, value: ''},
            mail: {type: 'email', title: 'E-mail', required: true, readonly: false, hint: 'The invoice is sent to you via the specified email address', value: ''},
            address: {type: 'text', title: 'Street address', required: true, readonly: false, value: ''},
            zipCode: {type: 'text', title: 'Zip code', required: true, readonly: false, value: ''},
            city: {type: 'text', title: 'Postal town', required: true, readonly: false, value: ''}
        },
        companyInvoice: {
            enable: {type: 'checkbox', title: 'Company invoice', required: false, readonly: false, value: ''},
            name: {type: 'text', title: 'Company name', required: true, autofocus: true, readonly: false, value: ''},
            mail: {type: 'email', title: 'Company e-mail', required: true, readonly: false, hint: 'The invoice is sent to you via the specified email address', value: ''},
            address: {type: 'text', title: 'Street address', required: true, readonly: false, value: ''},
            zipCode: {type: 'text', title: 'Zip code', required: true, readonly: false, value: ''},
            city: {type: 'text', title: 'Postal town', required: true, readonly: false, value: ''}
        },

        cancelRulesAccepted: false,
        showCancelRules: false,
        swishToken: null,
        swishTransactionId: null,
        showSwishDialog: false,
        multiUserPurchase: false,
        summaryWidth: 0,
        hasExpiredItems: false,
        beginCheckoutReported: false,
        epassiTransactionId: null,
        purchaseEventSent: false
    }),

    computed: {
        ...mapGetters(['selectedSiteId', 'selectedLegalEntityId']),
        effectiveLegalEntityId() {
            if (this.legalEntityId) return this.legalEntityId;

            // Derive the legal entity from the cart items' site — that is the
            // legal entity the backend actually charges (and validates saved
            // cards against) at checkout. The globally selected site may be a
            // different one than the shop being browsed, so it must NOT take
            // priority here. getSiteIdByItems already falls back to the
            // selected site when items carry no site_id.
            const siteId = this.getSiteIdByItems(this.itemsAndCartItems);
            const legalEntity = this.$store.getters.getLegalEntityBySiteId(siteId);
            return legalEntity?.id || this.selectedLegalEntityId || this.$store.state.settings?.default_legal_entity_id || null;
        },
        hasMultipleLegalEntities() {
            const ids = new Set(
                (this.$store.state.settings?.sites || [])
                    .map(s => s.legal_entity?.id)
                    .filter(Boolean)
            );
            return ids.size > 1;
        },
        cartLegalEntityName() {
            // Name of the legal entity the cart is tied to (via its items' site).
            // Shown so the customer can see which company they are buying from
            // when several companies exist — see hasMultipleLegalEntities.
            const siteId = this.getSiteIdByItems(this.itemsAndCartItems);
            return this.$store.getters.getLegalEntityBySiteId(siteId)?.name || '';
        },
        hasWebShop() {
            return !!this.$store.state.settings?.modules?.WebShop;
        },
        hasPayment() {
            return !!this.$store.state.settings?.modules?.Payment;
        },
        isMobileWithSwish() {
            return isMobile();
        },
        swishQR() {
            return this.$api.getAssetUrl('/api/payment/swish/qr') + '?format=png&size=300&token=' + this.swishToken + '&transaction_id=' + this.swishTransactionId;
        },
        selectedMethod() {
            return this.filteredMethods.find(x => x.id == this.selectedMethodId);
        },
        cartHasContinuousPayment() {
            return this?.totalFromServerObject?.subscriptions?.length > 0;
        },
        valueCardAmount() {
            if (!this.$store.state.user?.value_card_amounts) {
                return 0;
            }

            let legalEntityId = this.effectiveLegalEntityId;
            if (!legalEntityId) {
                const siteId = this.getSiteIdByItems(this.itemsAndCartItems);
                const site = this.$store.state.settings?.sites?.find(s => s.id === siteId);
                legalEntityId = site?.legal_entity?.id || this.$store.state.settings?.default_legal_entity_id;
            }

            if (!legalEntityId) {
                return 0;
            }

            return this.$store.state.user.value_card_amounts[legalEntityId] || 0;
        },
        mustSelectUsers() {
            return !!this.computedItems.filter(x => x.product && (x.product.type == 'trainingcard' || x.product.type == 'membership')).find(i => {
                for (let k=0; k < i.count; ++k) {
                    if (!i.users[k]) {
                        return true;
                    }
                }
            })
        },
        mustSelectIncludedProductVariants() {
            if (!this.$api.hasFlag('flag-package-trainingcard')) return false;
            let allProducts = this.$store.getters.allProducts;
            return !!this.computedItems.find(item => {
                if (!item.product?.included_products?.length) return false;
                let requireVariant = item.product.included_products
                    .map(id => allProducts.find(p => p.id == id))
                    .filter(p => p && p.variants && p.variants.length > 1);
                if (!requireVariant.length) return false;
                for (let k = 0; k < item.count; k++) {
                    let variantMap = item.variants_for_included_products?.[k] || {};
                    if (requireVariant.find(p => !variantMap[p.id])) return true;
                }
                return false;
            });
        },
        mustSelectUsersIndices() {
            let indices = [];

            this.visibleItems.filter(x => x.product && (x.product.type == 'trainingcard' || x.product.type == 'membership')).forEach(i => {
                for (let k=0; k < i.count; ++k) {
                    if (!i.users[k]) {
                        indices.push(this.visibleItems.indexOf(i));
                        return true;
                    }
                }
            })

            return indices;
        },
        readyToPay() {
            if (this.hasWebShop && (this.gettingTotalsFromServer > 0 || isNaN(this.totalFromServer))) {
                return false;
            }

            if (this.computedItems.length == 0) {
                return false;
            }

            if (this.cancelRules && !this.cancelRulesAccepted) {
                return false;
            }

            if (this.mustSelectUsers) {
                return false;
            }

            if (this.mustSelectIncludedProductVariants) {
                return false;
            }

            if (this.invalidPurchaseAgeItems.length) {
                return false;
            }

            if (this.hasExpiredItems) {
                return false;
            }

            if (this.totalFromServer == 0 && !this.cartHasContinuousPayment) {
                return true;
            }

            if (this.hasWebShop) {
                if (!this.formValid) {
                    return false;
                }

                if (!this.selectedMethod) {
                    return false;
                }

                if (this.selectedMethodId == 'stripe' || this.selectedMethodId == 'adyen-card') {
                    return this.cardValid;
                }

                if (this.selectedMethodId == 'directdebit') {
                    if (this.legalEntityId && this.$store.state.user?.consents) {
                        return !!this.$store.state.user.consents[this.legalEntityId];
                    }
                    return this.$store.state.user?.hasConsent();
                }

                if (this.selectedMethodId == 'stripe_saved') {
                    return !!this.savedCard?.last4;
                }

                if (this.selectedMethodId == 'invoice') {
                    return this.ageOkForInvoice;
                }
            }

            return true;
        },
        readyToPayExceptPaymentMethod() {
            if (this.hasWebShop && (this.gettingTotalsFromServer > 0 || isNaN(this.totalFromServer))) {
                return false;
            }

            if (this.computedItems.length == 0) {
                return false;
            }

            if (this.cancelRules && !this.cancelRulesAccepted) {
                return false;
            }

            if (this.mustSelectUsers) {
                return false;
            }

            if (this.mustSelectIncludedProductVariants) {
                return false;
            }

            if (this.invalidPurchaseAgeItems.length) {
                return false;
            }

            return true;
        },
        invalidPurchaseAgeItems() {
            if (!this.$store.state.settings?.modules?.WorkoutBookingRules) return [];
            let user = this.$store.state.user;
            if (!user) return [];
            let canBookFor = user.canBookFor || [];
            return this.computedItems.filter(item => {
                let purchaseAge = item.product?.purchase_age;
                if (!purchaseAge) return false;
                if (!item.users || !item.users.length) {
                    if (!user.birthdate) return true;
                    let age = Date.getAge(user.birthdate);
                    return age < Number(purchaseAge.from) || age > Number(purchaseAge.to);
                }
                return item.users.slice(0, item.count).some(userId => {
                    let u = userId ? canBookFor.find(x => x.id == userId) : user;
                    if (!u || !u.birthdate) return true;
                    let age = Date.getAge(u.birthdate);
                    return age < Number(purchaseAge.from) || age > Number(purchaseAge.to);
                });
            });
        },
        ageOkForInvoice() {
            let user = this.$store.state.user;
            let pcode = user.personalCodeNumber || this.invoice.personalCodeNumber.value;
            let age = getAgeFromPersonalCodeNumber(pcode);
            return user.company || age >= 18;
        },
        itemsContainValueCardOrGiftCard() {
            return this.computedItems.find(item => item.type == 'valuecard' || item.type == 'giftcard');
        },
        itemsContainPeriodicCards() {
            return this.computedItems.find(x => x.product && x.product.priceType && x.product.priceType != 'OneTime')
        },
        itemsAndCartItems() {
            if (this.useCart) {
                return this.items.concat(getCartItems(this.cartName));
            } else {
                return this.items;
            }
        },
        currentSiteId() {
            return this.getSiteIdByItems(this.itemsAndCartItems);
        },
        computedItems() {
            let res = [];
            let total = 0;
            if (this.itemsAndCartItems) {
                this.itemsAndCartItems.forEach(x => {
                    let o = Object.assign({}, x);
                    if (!o.count) {
                        o.count = 1;
                    }
                    o.fromCart = !this.items.includes(x);

                    if (o.product_id) {
                        if (!this.allProducts) {
                            return;
                        }

                        let product = this.allProducts.find(x => x.id == o.product_id);
                        if (!product) {
                            return;
                        }

                        o.price = product.payWhatYouWant ? o.price : product.price;
                        o.name = x.name || product.name;
                        o.imagekey = product.imagekey;
                        o.type = 'product';
                        o.product = product;

                        if (o.product && ['trainingcard', 'membership'].includes(o.product.type) && !o.users) {
                            o.users = new Array(o.count).fill(null);
                        }

                        if (o.product && ['trainingcard', 'membership'].includes(o.product.type)) {
                            if (!o.start_dates) {
                                o.start_dates = new Array(o.count).fill(null);
                            }
                            for (let i=0; i<o.start_dates.length; ++i) {
                                if (!o.start_dates[i]) {
                                    o.start_dates[i] = this.$booking.getStartDateForCard(o.product, o.users[i]).yyyymmdd();
                                }
                            }
                        }

                        if (o.product && o.product.included_products && o.product.included_products.length) {
                            if (!o.variants_for_included_products || !Array.isArray(o.variants_for_included_products)) {
                                o.variants_for_included_products = new Array(o.count).fill(null).map(() => ({}));
                            }
                        }
                    }

                    let count = o.count;
                    let punchcard;
                    if (x.rbservice_id) {
                        count *= x.slots.length;
                        o.context = x.context;
                        o.imagekey = x.service.imagekey;
                        o.name = x.service.name;
                        o.type = 'resourcebooking';
                        o.price = x.service.price;
                        const alreadyAddedPunchCards = res.filter(x => x.type == 'punchcard' && x.card.resourcebookingservice_id == x.rbservice_id);
                        let punchesInCart = {};
                        alreadyAddedPunchCards.forEach(x => {
                            if (x.card.id in punchesInCart) {
                                punchesInCart[x.card.id] += x.count;
                            } else {
                                punchesInCart[x.card.id] = x.count;
                            }
                        })

                        punchcard = this.getPunchCard(x.slots[0], count, punchesInCart);
                    }

                    if (x.teamsportregistration && x.price) {
                        o.price = x.price;
                    }

                    if (x.payment) {
                        let numProducts = x.payment.payments.length;
                        o.name = numProducts == 1 ? x.payment.payments[0].name : `${numProducts} produkter`;
                        o.type = 'payment';
                        o.price = x.payment.payments.reduce((sum, x) => sum + Number(x.amount), 0);
                        o.total = o.price;
                        o.payments = x.payment.payments;
                        o.imagekey = '/assets/img/cash.png';
                    }

                    o.total = count * Number(o.price);
                    total += o.total;

                    if (x.workout_bookings) {
                        x.workout_bookings.forEach(wb => {
                            res.push({type: 'workoutbooking', workout: wb.workout_id, imagekey: wb.workout.workoutType.imagekey, name: this.$translate('Booking of') + ': ' + wb.workout.workoutType.name, count: 1, parentProductMethods: o.product?.methods});
                        })
                    } else if (x.workout_id) {
                        res.push({type: 'workoutbooking', workout: x.workout_id, imagekey: x.workout.workoutType.imagekey, name: this.$translate('Booking of') + ': ' + x.workout.workoutType.name, count: 1, parentProductMethods: o.product?.methods});
                    }

                    if (o.product_id || x.rbservice_id || x.payment) {
                        res.push(o)
                    }

                    if (punchcard) {
                        o.punchcard = punchcard.id;
                        let imagekey = punchcard.cardtype.imagekey || '/assets/img/punchcard.png';
                        let name = punchcard.cardtype_name + ` (${punchcard.trainingsLeft} ${this.$translate('left')})`;

                        res.push({count: count, price: -o.price, total: -o.price * count, name, imagekey, type: 'punchcard', card: punchcard });
                        total -= o.price;
                    }

                    if (this.$api.hasFlag('flag-one-time-fee') && o.product && o.product.onetime_fee_product && Number(o.product.onetime_fee_product.price) > 0) {
                        let feeProduct = o.product.onetime_fee_product;
                        let feeTotal = Number(feeProduct.price) * count;
                        o.onetime_fee = {
                            count: count,
                            price: Number(feeProduct.price),
                            total: feeTotal,
                            name: feeProduct.name || this.$translate('One-time fee'),
                            imagekey: feeProduct.imagekey
                        };
                        o.total += feeTotal;
                        total += feeTotal;
                    }
                });
            }

            this.addedDiscounts.forEach((o) => {
                if (this.totalFromServerObject && this.totalFromServerObject.discount_details) {
                    if (o.source == 'price_rule') {
                        // Price-rule discount: match by code in discount_details
                        const matchingDiscounts = this.totalFromServerObject.discount_details.filter(x => x.source == 'price_rule' && (x.id == o.rule_id || x.code == o.code));
                        const discount = matchingDiscounts[0];
                        let discount_amount = matchingDiscounts.reduce((sum, x) => sum + x.amount, 0);
                        let name = o.rule_name || discount?.name || o.code;
                        res.push({count: 1, name, price: -discount_amount, total: -discount_amount, type: 'discount', source: 'price_rule', key: 'pr_code_' + o.code, code: o.code, hidden: o.hidden, notValid: discount ? false : true, notChecked: o.notChecked, rule_id: o.rule_id});
                        total -= discount_amount;
                    } else {
                        // Legacy discount: match by prod_id == cardtype_id
                        let discount = this.totalFromServerObject.discount_details.find(x => x.prod_id == o.cardtype_id);
                        let discount_amount = discount ? discount.amount : 0;
                        let fixed = Number(o.fixed || 0);
                        res.push({count: 1, name: o.cardtype_name, price: -discount_amount, total: -discount_amount, type: 'discount', fixed, percent: o.percent, imagekey: o.imagekey, cardtype_id: o.cardtype_id, key: o.cardtype_id, code: o.code, hidden: o.hidden, notValid: discount ? false : true, notChecked: o.notChecked});
                        total -= discount_amount;
                    }
                }
            })

            // Render automatic price-rule discounts (no code needed — server applies them automatically)
            if (this.totalFromServerObject?.discount_details) {
                const renderedCodes = new Set(this.addedDiscounts.filter(x => x.source == 'price_rule').map(x => x.code));
                this.totalFromServerObject.discount_details
                    .filter(x => x.source == 'price_rule')
                    .filter(x => !x.code || !renderedCodes.has(x.code)) // Don't double-render code-based discounts
                    .forEach(dd => {
                        // Skip if this rule was already rendered above as a code-based discount
                        if (res.find(r => r.rule_id == dd.id && r.type == 'discount')) return;
                        res.push({count: 1, name: dd.name, price: -dd.amount, total: -dd.amount, type: 'price_rule_discount', key: 'pr_' + dd.id, rule_id: dd.id});
                        total -= dd.amount;
                    });
            }

            this.addedGiftCards.forEach((o) => {
                let imagekey = o.imagekey || this.$api.getAssetUrl('/assets/img/giftcard.png');
                let price = Number(o.card.amount);
                let fromGiftCard = Math.min(total, price);
                let left = Math.max(price - total, 0);
                total -= fromGiftCard;
                res.push({count: 1, name: o.card.name, left, price: -fromGiftCard, total: -fromGiftCard, imagekey, card: o.card, type: 'giftcard', key: o.card.id, code: o.code, hidden: o.hidden });
            })

            let hasValueCard = !!res.find(x => x.product?.valuecard)

            if (total > 0 && !this.valueCardRemoved && this.$store.state.user && this.valueCardAmount > 0 && !hasValueCard) {
                let fromValueCard = Math.min(this.valueCardAmount, total);
                let imagekey = this.$api.getAssetUrl('/assets/img/valuecard.png');
                // FIXME: Get image from payment method

                if (this.valueCardCannotBeUsed) {
                    res.push({count: 1, name: this.$translate('Value card'), left: this.valueCardAmount, price: 0, total: 0, imagekey, type: 'valuecard' });
                } else {
                    let left = Math.max(this.valueCardAmount - total, 0);
                    res.push({count: 1, name: this.$translate('Value card'), left, price: -fromValueCard, total: -fromValueCard, imagekey, type: 'valuecard' });
                    total -= fromValueCard;
                }
            }

            const punchcards = res.filter(x => x.type == 'punchcard');
            punchcards.forEach(p => {
                const sameCard = punchcards.filter(x => x.card.id == p.card.id);
                const punchesFromCard = sameCard.reduce((sum, x) => sum + x.count, 0);
                p.leftAfterPurchase = p.card.trainingsLeft - punchesFromCard;
            });

            return res;
        },
        visibleItems() {
            return this.computedItems.filter(i => !i.hidden && !i.notValid);
        },
        visibleItemsTop() {
            return this.visibleItems.filter(x => !x.fromCart).sort((a, b) => (b.type === 'price_rule_discount') - (a.type === 'price_rule_discount'))
        },
        visibleItemsFromCart() {
            return this.visibleItems.filter(x => x.fromCart)
        },
        allProducts() {
            return this.$store.getters.allProducts;
        },
        isLoadingProducts() {
            return this.$store.state.productsLoading;
        },
        submitItems() {
            if (this.allProducts.length == 0 && this.itemsAndCartItems?.find(x => x.product_id)) {
                return [];
            }

            let res = this.itemsAndCartItems ? this.itemsAndCartItems.map(x => {
                let y = Object.assign({}, x);
                delete y.context;
                delete y.service;
                delete y.product;
                delete y.workout;
                return y;
            }) : [];

            // Filter out booking until product selected
            res = res.filter(i => !(!i.product_id && i.workout_bookings));

            res = res.filter(i => !(i.product_id && i.value)); // Filter out discount codes

            // [{users: [1,2,3]}] => {user_id: 1}, {user_id: 2}, {user_id: 3}
            let u = res.filter(i => this.productRequiresUser(i.product_id) || i.workout_id);
            res = res.filter(i => !this.productRequiresUser(i.product_id) && !i.workout_id);
            u.forEach(i => {
                i.users?.slice(0, i.count).forEach((user_id, index) => {
                    let ii = Object.assign({}, i);
                    ii.user_id = user_id;
                    ii.count = 1;
                    ii.start_date = i.start_dates ? i.start_dates[index] : null;
                    ii.variants_for_included_products = i.variants_for_included_products ? i.variants_for_included_products[index] : null;
                    res.push(ii);
                })
            })
            res.forEach(x => {
                delete x.users;
                delete x.start_dates;
            })

            // Split gift cards into individual rows
            u = res.filter(i => this.productIsGiftCard(i.product_id));
            res = res.filter(i => !this.productIsGiftCard(i.product_id));
            u.forEach(i => {
                for (let x=0; x < i.count; ++x) {
                    let ii = Object.assign({}, i);
                    ii.count = 1;
                    res.push(ii);
                }
            })

            let payments = res.filter(i => i.payment);
            res = res.filter(i => !i.payment);
            payments.forEach(p => {
                let payment_ids = [];
                p.payment.payments.forEach(payment => {
                    payment.payments.forEach(pid => {
                        if (!payment_ids.includes(pid)) {
                            payment_ids.push(pid);
                        }
                    })
                })

                res.push({payments: payment_ids})
            })

            this.computedItems.filter(x => x.type == 'giftcard').forEach((x) => {
                res.push({value: x.code, type: 'giftcard'});
            });

            this.computedItems.filter(x => x.type == 'valuecard' && x.total != 0).forEach(() => {
                res.push({type: 'valuecard'});
            });

            this.computedItems.filter(x => x.type == 'discount').forEach((x) => {
                if (x.source == 'price_rule') {
                    // Price-rule codes: send value only (no product_id). Backend extracts discount_codes from items with value.
                    res.push({value: x.code});
                } else {
                    res.push({product_id: x.cardtype_id, value: x.code});
                }
            });

            this.computedItems.filter(x => x.type == 'punchcard').forEach((x) => {
                res.push({type: 'punchcard', card_id: x.card.id});
            });

            return res;
        },
        itemsForTotal() {
            return this.submitItems.map(x => {
                x = Object.assign({}, x);
                delete x.answers;
                delete x.extended_info;
                return x;
            })
        },
        amountFromValueCard() {
            let vc = this.computedItems.find(x => x.type == 'valuecard');
            return vc ? -vc.price : 0;
        },
        savedCardText() {
            let card = this.savedCard;
            if (card && card.last4) {
                let month = String(card.exp_month);
                if (month.length == 1) {
                    month = '0' + month;
                }
                return this.$translate('SAVED_CARD').format(card.exp_year, month, card.last4);
            } else {
                return null;
            }
        },
        userHasAllInfoForInvoicePurchase() {
            let u = this.$store.state.user;
            if (!u) {
                return false;
            }

            return u.personalCodeNumber && u.address && u.zipCode && u.city && (u.mail || u.invoiceMail);
        },
        contactinfo() {
            return this.$store.state.settings ? this.$store.state.settings.contactinfo : {};
        },
        methodIdForServer() {
            if (this.selectedMethodId == 'stripe_saved') return 'stripe';
            if (this.selectedMethodId?.startsWith('adyen-card:saved')) return 'adyen-card';
            return this.selectedMethodId;
        },
        canBuyFor() {
            if (!this.$store.state.user) {
                return [];
            }

            return this.$store.state.user.canBookFor.map(x => ({
                id: x.id,
                name: x.id == this.$store.state.user.id ? this.$translate('Myself') : x.name,
                imagekey: x.imagekey
            }))
        },
        requirePhoneField() {
            let fields = this.$store.state?.settings?.fields;
            return !!fields?.find(x => x.builtIn && x.required && x.name == 'phone');
        },
        invoiceValueCard() {
            return this.$store.state?.settings?.invoiceValueCard;
        },
        discountErrorText() {
            if (this.multiDiscountError) {
                return this.$translate('Too many discount codes');
            } else if (this.wrongLegalEntityError) {
                return this.$translate('Gift card belongs to a different legal entity');
            } else if (this.codeError) {
                return this.$translate('No valid discount code or gift card');
            } else if (this.discountError) {
                return this.$translate('The discount code {0} is not valid for anything in the shopping cart').format(this.discountErrorCode);
            } else {
                return null;
            }
        },
        canEnterDiscountOrGiftcard() {
            return this.$store.state.isLoggedIn;
        }
    },

    watch: {
        '$store.state.user': {
            immediate: true,
            handler(user) {
                if (user?.savedCard) {
                    this.$api.get('/api/memberapi/paycard/get').then(card => {
                        this.savedCard = card;
                    })
                }
                if (user?.company) {
                    this.invoice.name.title = this.$translate('Company name');
                }
            }
        },
        useCart: {
            immediate: true,
            handler(useCart) {
                if (useCart) {
                    let cartItems = this.$store.state.carts?.[this.cartName]?.items ?? [];
                    if (cartItems.find(x => x.product_id)) {
                        this.loadProducts();
                    }
                }
            }
        },
        items: {
            immediate: true,
            handler(items) {
                if (items?.find(x => x.product_id)) {
                    this.loadProducts();
                }

                if (items) {
                    let codeItems = items.filter(i => i.value && i.product_id);
                    codeItems.forEach(codeItem => {
                        this.addCodeSilently(codeItem.value, codeItem.hidden);
                    })
                }
            }
        },
        expandFirstItem: {
            immediate: true,
            handler(expand) {
                if (expand) {
                    this.expandedItems = [0];
                }
            }
        },
        itemsForTotal: {
            immediate: true,
            handler() {
                this.getTotalsFromServer();
            }
        },
        'selectedMethodId': {
            immediate: true,
            handler() {
                this.getTotalsFromServer();

                if (this.selectedMethodId == 'invoice') {
                    this.invoiceChangeAddress = !this.userHasAllInfoForInvoicePurchase;
                }

                this.$emit('paymentMethod', this.selectedMethodId);
            }
        },
        done(done) {
            if (done && !this.purchaseEventSent) {
                let bundle_id = this.orderconfirmation && this.orderconfirmation.length && this.orderconfirmation[0].bundle_id;
                if (bundle_id) {
                    this.$emit('receipt', bundle_id);

                    try {
                        const ecommerceTrackingItem = this.ecommercePurchaseHelper(this.computedItems);
                        this.$ecommercetracking.purchase(bundle_id, ecommerceTrackingItem);
                        this.purchaseEventSent = true;
                    } catch(error) {
                        Sentry.captureException(error);
                    }
                }

                if (!this.newStyleDoneDialog) {
                    this.$emit('almostdone');
                } else {
                    this.$emit('done');
                    this.$store.commit('showCheckoutDoneDialog', {orderconfirmation: this.orderconfirmation, paidByMethod: this.paidByMethod, totalFromServer: this.totalFromServer })
                }

                if (this.useCart) {
                    this.$store.dispatch('removeCart', {cartName: this.cartName})
                }
            }
        },
        forceDone: {
            immediate: true,
            handler(forceDone) {
                if (forceDone && !this.done) {
                    this.done = true;
                }
            }
        },
        forceLoading: {
            immediate: true,
            handler(forceLoading) {
                this.loading = forceLoading;
            }
        },
        loading: {
            immediate: true,
            handler(value) {
                this.$emit('loading', value);
                if (this.selectedMethodId == 'swishi') {
                    this.showSwishDialog = value;
                }
                if (!value) {
                    this.swishToken = null;
                }
            }
        },
        totalFromServer(total) {
            this.$emit('cost', total);
        },
        readyToPay: {
            immediate: true,
            handler() {
                this.$emit('readyToPay', this.readyToPay);
            }
        },
        readyToPayExceptPaymentMethod: {
            immediate: true,
            handler() {
                this.$emit('readyToPayExceptPaymentMethod', this.readyToPayExceptPaymentMethod);
            }
        },
        mustSelectUsers: {
            immediate: true,
            handler() {
                this.$emit('mustSelectUsers', this.mustSelectUsers);
            }
        },
        invoiceChangeAddress(change) {
            if (change) {
                let u = this.$store.state.user;
                this.invoice.name.value = getName(u);
                this.invoice.mail.value = u.mail;
                this.invoice.address.value = u.address;
                this.invoice.zipCode.value = u.zipCode;
                this.invoice.city.value = u.city;
            }
        },
        filteredMethods: {
            immediate: true,
            handler(methods) {
                if (!this.computedItems.length || !this.$store.state.user || !this.methods || this.methods.length == 0) {
                    return;
                }

                if (methods.length == 0 && !this.valueCardRemoved && !isNaN(this.totalFromServer) && this.totalFromServer > 0 && this.valueCardAmount < this.totalFromServer) {
                    // There are no methods, we have added a value card automatically. Try removing it
                    this.valueCardCannotBeUsed = true;
                }
            }
        },
        itemsContainPeriodicCards: {
            immediate: true,
            handler(periodic) {
                if (periodic) {
                    this.valueCardCannotBeUsed = true;
                }
            }
        },
        showSummary: {
            immediate: true,
            handler(show) {
                if (show) {
                    this.$nextTick(() => {
                        if (this.$refs.summary) {
                            this.summaryWidth = this.$refs.summary.clientWidth - 16;
                        }
                    })

                }
            }
        },
        computedItems: {
            immediate: true,
            handler(items) {
                if (items && items.length) {
                    this.possiblyReportBeginCheckout();
                }
            }
        },
        sortedMethods: {
            immediate: true,
            handler(methods) {
                if (methods.length === 1 && this.selectedMethodId !== methods[0].id) {
                    this.selectedMethodId = methods[0].id;
                }
            }
        },
        currentSiteId: {
            immediate: true,
            handler() {
                this.loadPaymentMethods();
            }
        }
    },

    methods: {
        formatPrice,
        loadPaymentMethods() {
            // Extract payment IDs from cart items for backend filtering
            const paymentIds = this.extractPaymentIds(this.computedItems);

            // Use either payment_ids or legal_entity_id, not both
            const params = paymentIds.length > 0
                ? { payment_ids: paymentIds.join(',') }
                : { legal_entity_id: this.effectiveLegalEntityId };

            this.$api.get('/api/public/webshop/paymentmethod/get', params).then(methods => {
                this.methods = methods;
            });
        },
        loadProducts() {
            this.$store.dispatch('loadProducts')
        },
        extractPaymentIds(items) {
            const paymentIds = [];
            const paymentItems = items?.filter(item => item.payment) || [];

            paymentItems.forEach(item => {
                if (item.payment?.payments) {
                    item.payment.payments.forEach(paymentData => {
                        if (paymentData.payments && Array.isArray(paymentData.payments)) {
                            paymentIds.push(...paymentData.payments);
                        }
                    });
                }
            });

            return paymentIds;
        },
        cardChanged(object) {
            this.cardValid = !!object;
        },
        updateMemberInformationIfNeeded() {
            let user = this.$store.state.user;
            let data = {};
            let props = [/*'name' */'mail', 'personalCodeNumber', 'address', 'zipCode', 'city'];
            let needUpdate = false;

            if (this.selectedMethodId == 'invoice') {
                if (this.invoiceChangeAddress) {
                    props.forEach(prop => {
                        if (this.invoice[prop].value != user[prop]) {
                            data[prop] = this.invoice[prop].value;
                            needUpdate = true;
                        }
                    })
                }
            } else {
                if (!user.mail && this.mailField.value != user.mail) {
                    data.mail = this.mailField.value;
                    needUpdate = true;
                }
                if (this.requirePhoneField && !user.phone && this.phoneField.value != user.phone) {
                    data.phone = this.phoneField.value;
                    needUpdate = true;
                }
            }

            if (needUpdate) {
                this.$api.post('/api/memberapi/change', data).then(() => {
                    this.completePurchase(false);
                });
            } else {
                this.completePurchase(false);
            }
        },
        completePurchase(updateMember=true) {
            if (updateMember) {
                this.updateMemberInformationIfNeeded();
                return
            }

            let option = {
                use_valuecard: this.amountFromValueCard,
                use_giftcards: this.computedItems.filter(c => c.type == 'giftcard').map(gc => ({used_amount: -gc.price, card_id: gc.card.id}))
            };
            // The rules this cart was priced with. The server refuses the
            // purchase if the set no longer matches, rather than silently
            // charging a total nobody agreed on. Always sent, empty included —
            // a missing list means "does not report", which skips the check.
            option.expected_pricerule_ids = [...new Set(
                (this.totalFromServerObject?.discount_details || [])
                    .filter(d => d.source == 'price_rule' && d.id)
                    .map(d => d.id)
            )];
            if (this.selectedMethodId == 'swishi') {
                option.phone = null;
            }
            if (this.selectedMethodId == 'stripe') {
                option.another_card = this.$store.state.user.savedCard ? true : false;
                option.save_card = this.saveCard;
            }
            if (this.selectedMethodId == 'invoice' && this.companyInvoice.enable.value) {
                option.address_for_invoice = {
                    company_invoice: this.companyInvoice.enable.value,
                    name: this.companyInvoice.name.value,
                    mail: this.companyInvoice.mail.value,
                    address: this.companyInvoice.address.value,
                    zipCode: this.companyInvoice.zipCode.value,
                    city: this.companyInvoice.city.value
                }
            }

            let total = this.totalFromServer;
            let method = this.paidByMethod = this.totalFromServer > 0 || this.cartHasContinuousPayment ? this.methodIdForServer : null; // FIXME: Verify that correct
            let stripe_saved = false;
            if (this.selectedMethodId == 'stripe_saved') {
                method = 'stripe';
                stripe_saved = true;
            }
            let data = {method, total, items: this.submitItems, option};
            this.loading = true;
            this.orderconfirmation = null;
            this.adyenProcess = method?.startsWith('adyen-');

            if (this.selectedMethodId?.startsWith('adyen-') && this.totalFromServer > 0) {
                if (this.selectedMethodId.startsWith('adyen-card:saved')) {
                    option.use_saved_card = this.selectedMethod.ref;
                } else {
                    const adyen = this.$refs.adyen[0].getData();
                    option.save_card = adyen.saveCard;
                    option.adyen = adyen.data;
                }
            }

            this.$api.post('/api/public/webshop/checkout', data).then(cresponse => {
                cresponse.json().then(response => {
                    if (cresponse.ok) {
                        if(response.status === 'pending') {
                            console.log('Response is pending');

                            if (method == 'stripe') {
                                let stripe = this.$refs.stripe[0];
                                stripe.confirmCardPayment(response.client_secret, response.channel2, (response) => {
                                    console.log('Callback for confirmCardPayment');
                                    this.loading = false;
                                    if (response.success) {
                                        this.orderconfirmation = response.orderconfirmation;
                                        this.done = true;
                                        //this.$emit('done');
                                    } else {
                                        this.$store.commit('showSnackBar', {text: this.$translate(response.reason || 'Failed to perform purchase')});
                                    }
                                }, {stripe_saved});
                            } else if (method == 'swishi') {
                                this.swishToken = response.token;
                                this.swishTransactionId = response.transaction_id;
                                if (this.isMobileWithSwish) {
                                    this.openSwish();
                                }

                                let unsubscribeChannel = this.$api.subscribeChannel(response.channel2, (response) => {
                                    if (response.type === 'payment') {
                                        this.loading = false;
                                        if(response.success) {
                                            this.orderconfirmation = response.orderconfirmation;
                                            //this.$emit('done');
                                            this.done = true;
                                        } else {
                                            this.$store.commit('showSnackBar', {text: this.$translate(response.reason || 'Failed to perform purchase')});
                                        }
                                        unsubscribeChannel();
                                    }
                                });
                            } else if (method?.startsWith('adyen-')) {
                                let unsubscribeChannel = this.$api.subscribeChannel(response.channel2, (response) => {
                                    if (response.type === 'adyen:payment') {
                                        this.$refs.adyen[0].handle(response.action, response.secret);
                                    } else if (response.type === 'adyen:system-error') {
                                        this.loading = false;
                                        this.$store.commit('showSnackBar', {text: this.$translate('Failed to perform purchase')});
                                        this.$refs.adyen?.[0].reset();
                                        unsubscribeChannel();
                                    } else if (response.type === 'payment') {
                                        this.loading = false;
                                        if(response.success) {
                                            this.orderconfirmation = response.orderconfirmation;
                                            this.done = true;
                                        } else {
                                            this.$store.commit('showSnackBar', {text: this.$translate(response.reason || 'Failed to perform purchase')});
                                            this.$refs.adyen?.[0].reset();
                                        }
                                        unsubscribeChannel();
                                    }
                                });
                            } else if (method == 'epassi') {
                                this.epassiTransactionId = response.transaction_id;
                            }
                        } else if(response.status === 'done') {
                            this.loading = false;

                            this.orderconfirmation = response.orderconfirmation;
                            this.done = true;
                            //this.$emit('done');
                        }
                    } else {
                        let text = this.$translate(response.message);
                        if (response.type == 'double_trial_card') {
                            text = this.$translate('The product can only be purchased once');
                        } else if (response.type === 'phone_busy') {
                            text = this.$translate('The phone number is used for another purchase, cancel it first');
                        } else if (response.type === 'no_settings') {
                            text = this.$translate('Payment method is not ready to use');
                        } else if (response.type === 'error_and_valuecard') {
                            text = this.$translate('STRIPE_ERROR_AND_VALUECARD');
                        } else if (response.type === 'pricerule_no_longer_available'
                                || response.type === 'pricerule_limit_exceeded') {
                            // Someone took the last discount while this cart was
                            // open.
                            text = this.$translate('The discount is no longer available. The price has been updated.');
                            this.getTotalsFromServer(true);
                        } else if (response.type === 'pricerule_price_changed') {
                            // The opposite: a discount came back after this cart
                            // was priced.
                            text = this.$translate('The pricing has changed. The price has been updated.');
                            this.getTotalsFromServer(true);
                        }

                        this.$store.commit('showSnackBar', {text});
                        this.loading = false;
                    }
                });
            });
        },
        addCodeSilently(code, hidden) {
            const siteId = this.getSiteIdByItems(this.itemsAndCartItems);
            const site = this.$store.state.settings?.sites?.find(s => s.id === siteId);
            const legal_entity_id = site?.legal_entity?.id || this.$store.state.settings?.default_legal_entity_id;
            this.$api.get('/api/public/giftcard/verify', {code, legal_entity_id}).then(response => {
                if (response.found) {
                    if (response.paid && !response.expired && Number(response.card.amount) && !response.wrong_legal_entity) {
                        if (!this.addedGiftCards.find(x => x.code == code)) {
                            this.addedGiftCards.push({code, giftcard: true, card: response.card, hidden});
                        }
                    }
                } else {
                    this.addPriceRuleOrLegacyDiscountCode(code, {hidden, notChecked: true});
                }
            });
        },
        addLegacyDiscountCode(code, options = {}) {
            this.$api.get('/api/public/discount/verify', {code}).then(response => {
                if (response.found) {
                    if (!this.addedDiscounts.find(x => x.code == code || x.cardtype_id == response.cardtype_id)) {
                        if (!this.$store.state.settings?.allow_multiple_discounts && this.addedDiscounts.length >= 1) {
                            this.multiDiscountError = true;
                        } else {
                            this.addedDiscounts.push({
                                code,
                                discount: true,
                                cardtype_id: response.cardtype_id,
                                fixed: response.fixed,
                                percent: response.percent,
                                cardtype_name: response.cardtype_name,
                                imagekey: response.imagekey,
                                hidden: options.hidden,
                                notValid: true,
                                notChecked: options.notChecked,
                            });
                            this.codeError = false;
                            this.discountError = false;
                            this.multiDiscountError = false;
                        }
                    } else {
                        this.codeError = false;
                        this.discountError = false;
                    }
                } else {
                    this.codeError = true;
                }
                this.code = '';
                this.loadingCode = false;
            });
        },
        addPriceRuleOrLegacyDiscountCode(code, options = {}) {
            if (!this.$api.hasFlag || !this.$api.hasFlag('flag-price-rule-engine')) {
                this.addLegacyDiscountCode(code, options);
                return;
            }

            this.$api.get('/api/public/pricerule/verify_code', {code}).then(prResponse => {
                if (prResponse.valid) {
                    if (!this.addedDiscounts.find(x => x.code == code)) {
                        this.addedDiscounts.push({
                            code,
                            discount: true,
                            source: 'price_rule',
                            rule_id: prResponse.rule.id,
                            rule_name: prResponse.rule.name,
                            cardtype_id: null,
                            hidden: options.hidden,
                            notValid: true,
                            notChecked: options.notChecked,
                        });
                        this.codeError = false;
                        this.discountError = false;
                        this.multiDiscountError = false;
                    }
                    this.code = '';
                    this.loadingCode = false;
                } else {
                    this.addLegacyDiscountCode(code, options);
                }
            });
        },
        addCode() {
            this.loadingCode = true;
            let code = this.code;
            const siteId = this.getSiteIdByItems(this.itemsAndCartItems);
            const site = this.$store.state.settings?.sites?.find(s => s.id === siteId);
            const legal_entity_id = site?.legal_entity?.id || this.$store.state.settings?.default_legal_entity_id;
            this.$api.get('/api/public/giftcard/verify', {code, legal_entity_id}).then(response => {
                if (response.found) {
                    this.wrongLegalEntityError = false;
                    if (response.paid && !response.expired && Number(response.card.amount) && !response.wrong_legal_entity) {
                        if (!this.addedGiftCards.find(x => x.code == code)) {
                            this.addedGiftCards.push({code, giftcard: true, card: response.card});
                        }
                        this.code = '';
                        this.codeError = false;
                    } else if (response.wrong_legal_entity) {
                        this.code = '';
                        this.codeError = false;
                        this.wrongLegalEntityError = true;
                    } else {
                        this.code = '';
                        this.codeError = true;
                    }
                    this.loadingCode = false;
                } else {
                    this.addPriceRuleOrLegacyDiscountCode(code);
                }
            })
        },
        removeItem(item) {
            if (item.type == 'giftcard') {
                let index = this.addedGiftCards.findIndex(x => x.card.id == item.card.id);
                if (index != -1) {
                    this.addedGiftCards.splice(index,1);
                }
            } else if (item.type == 'discount') {
                let index;
                if (item.source == 'price_rule') {
                    index = this.addedDiscounts.findIndex(x => x.code == item.code && x.source == 'price_rule');
                } else {
                    index = this.addedDiscounts.findIndex(x => x.cardtype_id == item.cardtype_id);
                }
                if (index != -1) {
                    this.addedDiscounts.splice(index,1);
                }
            } else if (item.type == 'valuecard') {
                this.valueCardRemoved = true;

                // FIXME: This should probably go somewhere else
            } else if (item.fromCart) {
                if (item.reservation) {
                    this.$api.post('/api/resource/reservation/cancel', {key: item.reservation});
                }
                this.$store.dispatch('changeItemCountInCart', {cartName: this.cartName, product_id: item.product_id, variant: item.variant, count: 0, tmp_id: item.tmp_id});
            }
        },
        // `force` re-prices an unchanged cart.
        // Without it the re-price after those refusals is skipped and the
        // customer is told the price was updated when it was not.
        getTotalsFromServer(force = false) {
            if (!this.$store.state.settings?.modules.WebShop) {
                return;
            }

            if (!this.itemsForTotal.length) {
                return;
            }

            let data = {items: this.itemsForTotal, method: this.methodIdForServer};
            let valueUsed = JSON.stringify(data);
            if (!force && valueUsed == this.lastTotalsValueUsed) {
                return;
            }
            this.gettingTotalsFromServer += 1;
            this.$api.post('/api/public/webshop/total', data).then(r => {
                this.gettingTotalsFromServer -= 1;
                let correctCheck = JSON.stringify({items: this.itemsForTotal, method: this.methodIdForServer}) == valueUsed;
                if (!r.ok) {
                    if (correctCheck) {
                        this.totalFromServer = NaN;
                    }
                    r.json().then(response => {
                        let text = this.$translate(response.message);
                        if (response.message == 'Invalid product or customer cannot buy it') {
                            if (this.useCart) {
                                this.$store.dispatch('removeCart', {cartName: this.cartName})
                            }
                            this.$store.commit('showErrorDialog', {title: this.$translate('The shopping cart is broken'), text: this.$translate('The shopping cart has broken. You need to start over and add the products again.')});
                        } else {
                            this.$store.commit('showSnackBar', {text});
                        }
                    });
                } else {
                    r.json().then(x => {
                        x = decodeJson2(x);
                        if (correctCheck) {
                            this.totalFromServer = x.total;
                            this.totalFromServerObject = x;
                            this.lastTotalsValueUsed = valueUsed;

                            // Find all discounts that we have sent to the server for validation and are available locally.
                            const sharedDiscounts = this.computedItems.filter(item => this.addedDiscounts.map(d => d.cardtype_id).includes(item.cardtype_id));

                            // For all the items we have already sent to the server save invalid items.
                            const invalidDiscounts = sharedDiscounts.filter(item => item.notValid);

                            // notChecked flag is used to prevent race condition between the API
                            // of the webshop/total and the computedItems property.
                            if (invalidDiscounts.some(item => !item.notChecked)) {
                                this.discountError = true;
                                this.discountErrorCode = `"${invalidDiscounts[0].code}"`;

                                // Remove the invalid items from the addedDiscounts
                                this.addedDiscounts = this.addedDiscounts.filter(discount => !invalidDiscounts.some(invalidDiscount => invalidDiscount.cardtype_id === discount.cardtype_id));

                            }
                            // Disable the error message if valid code has been given.
                            else if (sharedDiscounts.length > 0) {
                                this.discountError = false;
                            }

                            // We consider discounts checked after the first invalid pass.
                            // This is because we know we have done computedItems at least once,
                            // and we are on the second pass to the webshop/total endpoint.
                            // If we remove the items earlier we might consider some valid discounts
                            // as invalid. Happens especially often using addCodeSilently.
                            if (this.invalidItems?.length > 0) {
                                this.addedDiscounts.forEach(discount => discount.notChecked = false);
                            }
                        }
                    });
                }
            })
        },
        getMethodName(method) {
            if (method.id == 'swishi') {
                return 'Swish'
            } else if (method.id == 'stripe') {
                if (this.filteredMethods.find(x => x.id == 'stripe_saved')) {
                    return 'Annat betalkort';
                }
                return this.$translate('Debit card');
            } else if (method.id == 'unknown') {
                return this.$translate('Pay on site');
            }
            return this.$translate(method.name);
        },
        getPunchCard(slot, count, punchesInCart) {
            let cards = this.$store.state.cards;
            const validCards = cards ? cards.filter(card => !card.membership && card.resourcebookingservice_id == slot.rbservice_id && this.$booking.isCardValid(card, Date.newFull(slot.start)) && count <= card.trainingsLeft - (punchesInCart[card.id] || 0)) : null;
            const today = new Date();

            // Sort the valid cards by validToDate. If the dates are the same, sort by price
            if (!validCards) { return null; }
            validCards.sort((a, b) => {
                const dateDifferenceA = Math.abs(today - a.validToDate);
                const dateDifferenceB = Math.abs(today - b.validToDate);

                if (dateDifferenceA === dateDifferenceB) {
                    return Number(b.price) - Number(a.price);
                }

                return dateDifferenceA - dateDifferenceB;
            });
            return validCards[0] || null;
        },
        logout() {
            this.$store.dispatch('logout');
        },
        attemptChangeNumberOfItems(item, count) {
            let alreadyAdded = 0;
            this.visibleItems.filter(i => i != item).forEach(i => {
                if (i.product_id == item.product_id && i.variant == item.variant) {
                    alreadyAdded += i.count;
                }
            })
            let product = this.allProducts.find(x => x.id == item.product_id);
            let stock = null;
            if (product.variants) {
                let variant = product.variants.find(v => v.name == item.variant)
                if (!variant) {
                    // Error
                }
                let ss = variant?.stock_sites.find(x => x.site_id == this.siteId)
                if (ss) {
                    stock = ss.stock !== null ? ss.stock - alreadyAdded : null;
                }
            }

            if (stock != null && count > stock) {
                let text = stock > 0 ? this.$translate('There is only {0} of this product in stock').format(stock) : this.$translate('There are no products left in stock');
                this.$store.commit('showSnackBar', {text});
            } else {
                if (item.fromCart) {
                    this.$store.dispatch('changeItemCountInCart', {cartName: this.cartName, product_id: item.product_id, variant: item.variant, count, tmp_id: item.tmp_id, product});
                } else {
                    this.$emit('changeNumberOfItems', item, count)
                }
            }
        },
        isValidPersonalCode(x) {
            return isValidPersonalCode(x);
        },
        getReceipt() {
            let id = this.orderconfirmation[0].bundle_id;
            if (isExpoApp()) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'openFile', data: { api: {url: 'current', path: '/api/memberapi/receipt/get', args: { id } }, type: 'pdf' } }));
            } else {
                this.$api.get('/api/memberapi/receipt/get', {id}, {blob: true}).then(x => {
                    downloadFile({filename: this.$translate('Receipt') + '.pdf', data: x, type: 'application/pdf'})
                })
            }
        },
        openSwish() {
            let callbackurl = '';
            if (isExpoApp() && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'swish', data: {swishtoken: this.swishToken, callbackurl: callbackurl} }));
            } else if (this.isMobileWithSwish) {
                window.open(`swish://paymentrequest?token=${this.swishToken}&callbackurl=${encodeURIComponent(callbackurl)}`);
            }
        },
        cancelSwish() {
            this.$api.post('/api/payment/swish/cancel', { transaction_id: this.swishTransactionId });
            this.showSwishDialog = false;
            this.loading = false;
        },
        productIsGiftCard(product) {
            product = isObject(product) ? product : this.allProducts.find(p => p.id == product);
            return product?.type == 'giftcard';
        },
        productRequiresUser(product) {
            product = isObject(product) ? product : this.allProducts.find(p => p.id == product);
            return product && ['membership', 'trainingcard'].includes(product.type);
        },
        getItemKey(item, prefix) {
            prefix = prefix || '';
            return prefix + (item.key || (item.type + item.product_id + item.variant + item.rbservice_id + item.tmp_id + item.course_id + item.user_id));
        },
        ecommercePurchaseHelper(items) {
            let ecommerceTrackingItems = [];
            for (let i = 0; i < items.length; i++) {
                let ecommerceTrackingItem = {};
                if (Object.prototype.hasOwnProperty.call(items[i], 'product') && items[i].product) {
                    ecommerceTrackingItem = JSON.parse(JSON.stringify(items[i].product));

                    if (Object.prototype.hasOwnProperty.call(items[i], 'course_id') && items[i].course_id) {
                        ecommerceTrackingItem.id = 'c' + items[i].course_id;
                    } else {
                        ecommerceTrackingItem.id = 'p' + ecommerceTrackingItem.id;
                    }

                    ecommerceTrackingItem.count = items[i].count;
                    ecommerceTrackingItems.push(ecommerceTrackingItem);
                } else if (Object.prototype.hasOwnProperty.call(items[i], 'service') && items[i].service) {
                    ecommerceTrackingItem =  JSON.parse(JSON.stringify(items[i].service));
                    ecommerceTrackingItem.id = 'r' + ecommerceTrackingItem.id;
                    ecommerceTrackingItem.count = items[i].count;
                    ecommerceTrackingItems.push(ecommerceTrackingItem);
                }
            }

            return ecommerceTrackingItems;
        },
        possiblyReportBeginCheckout() {
            if (!this.beginCheckoutReported) {
                const ecommerceTrackingItems = this.ecommercePurchaseHelper(this.computedItems);
                this.$ecommercetracking.beginCheckout(ecommerceTrackingItems);
                this.beginCheckoutReported = true;
            }
        },
        clearCartAndClose() {
            this.$store.dispatch('removeCart', { cartName: this.cartName });
            this.$emit('done');
        },
        getSiteIdByItems(items) {
            // If all items are from the same site, return that site. Otherwise return the selected site.
            let siteId, selectedSiteId = this.selectedSiteId;
            for (const item of items) {
                let id = item.site_id || selectedSiteId;
                if (siteId && siteId != id) return selectedSiteId;  // Different site ids
                siteId = id;
            }
            return siteId || selectedSiteId;
        }
    }
}
</script>
<!--

Till komponenten kan man skicka in:

- Varor som ska köpas
- Användaruppgifter som ska användas för att skapa konto
- Förkonfigurerat betalsätt
- Enkel registrering (namn+e-post?) eller full registrering

Fundering:

- Vad händer om det som skickats in inte är korrekt? T.ex. e-post redan registrerat, betalsättet inte möjligt att välja, användaruppgifter
  är inskickade trots att man är inloggad osv
  Alt 1 (lättast). Visa något felmeddelande i komponenten. Alt 2 (svårare). Visa fel + låt användaren korrigera i komponenten

- Är det betalsättet som är det primära och ska alla uppgifter hamna under det? Om man byter betalsätt efter att fyllt i uppgifter,
  vad ska hända då?

- Inställning för om "Redan kund?" ska visas?

-

Utcheckningskomponenten bör utökas med:

- Allmänna villkor
- Presentkort
- Rabattkod

Internt:

- Förenkla vad man skickar in. Går det att skapa en verifieringsfunktion så att man kan testa det man fyllt i?

  T.ex. $api.verifyCheckoutItems(items): bool

  submitItems: [{product_id: 1}]         index=0 -> visibleItems[1] -> computedItems[2]
  visibleItems: [{},{}]
  computedItems: [{},{},{}]

-->
